import DataTable, { TableProps, createTheme } from 'react-data-table-component';

export default function DatatableWithTheme(props: TableProps<any>) {
  const isDark = document.documentElement.classList.contains('dark');

  // Define dark theme (text + background)
  createTheme('dark-theme', {
    text: {
      primary: '#E5E7EB', // gray-200
      secondary: '#9CA3AF', // gray-400
    },
    background: {
      default: '#1F2937', // gray-800
    },
    context: {
      background: '#374151', // gray-700
      text: '#F9FAFB', // gray-50
    },
    divider: {
      default: '#374151', // gray-700
    },
    action: {
      button: 'rgba(255,255,255,.54)',
      hover: 'rgba(255,255,255,.08)',
      disabled: 'rgba(255,255,255,.12)',
    },
  });

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'transparent',
      },
    },
    headRow: {
      style: {
        backgroundColor: 'transparent',
        borderBottom: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`, // gray-800 / gray-200
        minHeight: '40px',
      },
    },
    headCells: {
      style: {
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em', // tracking-wider
        fontWeight: '600',
        color: isDark ? '#9ca3af' : '#6b7280', // gray-400 / gray-500
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '56px',
        backgroundColor: 'transparent',
        borderBottom: `1px solid ${isDark ? '#1f2937' : '#f3f4f6'}`, // gray-800 / gray-100
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(31, 41, 55, 0.5)' : '#f9fafb', // gray-800/50 / gray-50
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '13px',
        color: isDark ? '#d1d5db' : '#374151', // gray-300 / gray-700
      },
    },
    pagination: {
      style: {
        borderTop: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`, // gray-800 / gray-200
        backgroundColor: 'transparent',
        color: isDark ? '#9ca3af' : '#6b7280', // gray-400 / gray-500
        fontSize: '11px', // Smaller font size for pagination text
        minHeight: '44px',
      },
    },
  };

  return (
    <DataTable
      theme={isDark ? 'dark-theme' : 'default'}
      subHeader={false}
      selectableRows={props.selectableRows || false}
      persistTableHead
      customStyles={customStyles as any}
      paginationComponentOptions={{
        rowsPerPageText: 'Rows per page:',
        rangeSeparatorText: 'of',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'All',
      }}
      noDataComponent={
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-gray-400 mb-2">
            <svg
              className={'w-5 h-5'}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
            No data found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">Try adjusting your search criteria</p>
        </div>
      }
      {...props}
    />
  );
}
